import React, { useState, useEffect } from "react";
import "../Management/CustomerEngagement.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCompanyProfile } from "../Components/Context/CompanyProfileContext";
import customer from "../Components/Assets/Customer-Engage/customers.png";
import leads from "../Components/Assets/Customer-Engage/leads.png";
import lost from "../Components/Assets/Customer-Engage/lost.png";
import search_icn from "../Components/Assets/Customer-Engage/search.png";
import add_customer from "../Components/Assets/Customer-Engage/people.png";

export function CustomerEngagement() {
  const { hasGoogleSheetsAPI, getGoogleSheetsAPI } = useCompanyProfile();
  const [sheetData, setSheetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [editCustomerData, setEditCustomerData] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [newEntry, setNewEntry] = useState({
    "First Name": "",
    "Last Name": "",
    "Email id": "",
    "Mobile No": "",
    Stage: "Lead",
    "Lead Date": "",
    "Email Subscription": "Yes",
    "Other Details": "",
  });

  // Get Google Sheets script URL from company profile
  const GOOGLE_SCRIPT_URL = getGoogleSheetsAPI();

  // Early return if no Google Sheets API is configured
  if (!hasGoogleSheetsAPI()) {
    return (
      <div className="customer-engagement-page">
        <div
          style={{
            padding: "40px",
            textAlign: "center",
            backgroundColor: "#f8f9fa",
            margin: "20px",
            borderRadius: "8px",
          }}
        >
          <h2>Customer Engagement</h2>
          <p style={{ color: "#666", marginBottom: "20px" }}>
            Google Sheets API is not configured for your company profile.
          </p>
          <p style={{ color: "#666", marginBottom: "20px" }}>
            Please contact your administrator to configure the Google Sheets API
            integration.
          </p>
        </div>
      </div>
    );
  }

  // Fetch data from Google Sheets
  const fetchGoogleSheetsData = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "GET",
        mode: "cors",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Handle the data structure from your Google Apps Script
      if (data && data.data && Array.isArray(data.data)) {
        setSheetData(data.data);
      } else if (Array.isArray(data)) {
        setSheetData(data);
      } else {
        setSheetData([]);
      }
    } catch (error) {
      console.error("Error fetching Google Sheets data:", error);
      setError("Failed to load customer data: " + error.message);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchGoogleSheetsData();
  }, []);

  // Filter data based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(sheetData);
    } else {
      const filtered = sheetData.filter((item) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (item["First Name"] &&
            item["First Name"].toLowerCase().includes(searchLower)) ||
          (item["Last Name"] &&
            item["Last Name"].toLowerCase().includes(searchLower)) ||
          (item["Email id"] &&
            item["Email id"].toLowerCase().includes(searchLower)) ||
          (item["Mobile No"] &&
            item["Mobile No"].toString().includes(searchTerm)) ||
          (item["Stage"] &&
            item["Stage"].toLowerCase().includes(searchLower)) ||
          (item["Customer ID"] &&
            item["Customer ID"].toString().includes(searchTerm)) ||
          (item["Other Details"] &&
            item["Other Details"].toLowerCase().includes(searchLower))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, sheetData]);

  // Refresh data function
  const handleRefresh = () => {
    fetchGoogleSheetsData();
  };

  // Add new entry to Google Sheets
  const handleAddEntry = async (e) => {
    e.preventDefault();

    try {
      setAddLoading(true);
      setError(null);

      // Validate required fields
      if (
        !newEntry["First Name"] ||
        !newEntry["Last Name"] ||
        !newEntry["Email id"]
      ) {
        throw new Error(
          "Please fill in all required fields (First Name, Last Name, Email)"
        );
      }

      const requestBody = {
        ...newEntry,
        "Lead Date":
          newEntry["Lead Date"] || new Date().toLocaleDateString("en-US"),
        "Customer ID": "", // Will be auto-generated by the script
      };

      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      // Since we're using no-cors, we won't get response data
      setTimeout(() => {
        fetchGoogleSheetsData(true); // Silent refresh - don't show loading UI
        toast.success("Entry submitted! Refreshing data to verify...", {
          position: "top-right",
          autoClose: 3000,
          style: {
            backgroundColor: "#28a745",
            color: "white",
          },
        });
      }, 2000);

      // Reset form and close modal
      setNewEntry({
        "First Name": "",
        "Last Name": "",
        "Email id": "",
        "Mobile No": "",
        Stage: "Lead",
        "Lead Date": "",
        "Email Subscription": "Yes",
        "Other Details": "",
      });
      setShowAddForm(false);
    } catch (error) {
      console.error("Error adding entry:", error);
      setError("Failed to add entry: " + error.message);
      toast.error("Failed to add entry: " + error.message, {
        position: "top-right",
        autoClose: 5000,
        style: {
          backgroundColor: "#dc3545",
          color: "white",
        },
      });
    } finally {
      setAddLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEntry((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  // Handle customer row click to view details
  const handleCustomerRowClick = (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerDetails(true);
    setEditingCustomer(false);
  };

  // Handle edit customer
  const handleEditCustomer = (customer) => {
    setEditCustomerData(customer);
    setSelectedCustomer(customer);
    setShowCustomerDetails(true);
    setEditingCustomer(true);
  };

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditCustomerData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle save edited customer
  const handleSaveEdit = async () => {
    try {
      setUpdateLoading(true);
      setError(null);

      // Validate required fields
      if (!editCustomerData["Customer ID"]) {
        throw new Error("Customer ID is required for update");
      }

      const requestBody = {
        ...editCustomerData,
        action: "update", // This tells the Apps Script to handle as UPDATE
      };

      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      // Since we're using no-cors, we won't get response data
      setTimeout(() => {
        fetchGoogleSheetsData(true); // Silent refresh - don't show loading UI
        toast.success("Update submitted! Refreshing data to verify...", {
          position: "top-right",
          autoClose: 3000,
          style: {
            backgroundColor: "#28a745",
            color: "white",
          },
        });
      }, 2000);

      setEditingCustomer(false);
      setSelectedCustomer(editCustomerData);
    } catch (error) {
      console.error("Error updating customer:", error);
      setError("Failed to update customer: " + error.message);
      toast.error("Failed to update customer: " + error.message, {
        position: "top-right",
        autoClose: 5000,
        style: {
          backgroundColor: "#dc3545",
          color: "white",
        },
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  // Close customer details view
  const closeCustomerDetails = () => {
    setShowCustomerDetails(false);
    setSelectedCustomer(null);
    setEditingCustomer(false);
    setEditCustomerData({});
  };

  // Calculate stage counts
  const getStageCount = (stage) => {
    const dataToCount = searchTerm ? filteredData : sheetData;
    return dataToCount.filter(
      (item) =>
        item["Stage"] && item["Stage"].toLowerCase() === stage.toLowerCase()
    ).length;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="customer-engagement-page">
      <ToastContainer
        toastStyle={{
          color: "white",
          backgroundColor: "#333",
          fontSize: "14px",
        }}
      />

      {/* Header Section */}
      <div className="engagement-header">
        <div className="stage-counter-container">
          <div className="stage-counter-box">
            <div className="stage-icn">
              <img src={customer} alt="" />
            </div>
            <div className="stage-title">Customer</div>
            <div className="stage-count">{getStageCount("Customer")}</div>
          </div>
          <div className="stage-counter-box">
            <div className="stage-icn">
              <img src={leads} alt="" />
            </div>
            <div className="stage-title">Leads</div>
            <div className="stage-count">{getStageCount("Lead")}</div>
          </div>
          <div className="stage-counter-box">
            <div className="stage-icn">
              <img src={lost} alt="" />
            </div>
            <div className="stage-title">Lost</div>
            <div className="stage-count">{getStageCount("Lost")}</div>
          </div>
        </div>
      </div>
      <div className="header-action-container">
        <div className="header-actions">
          <input
            type="text"
            className="engage-search-input"
            placeholder="Search customers..."
            name="search"
            id="search"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <button className="search-btn">
            <img src={search_icn} alt="Search" />
          </button>
          {searchTerm && (
            <button
              className="clear-search-btn"
              onClick={clearSearch}
              style={{
                padding: "8px 12px",
                backgroundColor: "#dc3545",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                marginLeft: "5px",
                fontSize: "12px",
              }}
            >
              Clear
            </button>
          )}

          <button
            className="add-btn"
            onClick={() => setShowAddForm(!showAddForm)}
            disabled={addLoading}
            style={{}}
          >
            <img src={add_customer} alt="" />
            <h6>{showAddForm ? " Close Form" : " Add Customer"}</h6>
          </button>
          <button
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? "Loading..." : "🔄 Refresh Data"}
          </button>
        </div>
      </div>
      {/* Add Cutomer Section */}
      {showAddForm && (
        <div className="new-customer-section">
          <div className="new-customer-container">
            <div className="customer-close-btn">
              <div className="empty-div"></div>
              <div className="add-cutomer-title">Add Customer</div>
              <img
                width="48"
                height="48"
                src="https://img.icons8.com/fluency/48/delete-sign.png"
                alt="delete-sign"
                className="status-cross-symbol"
                onClick={() => setShowAddForm(false)}
                style={{ cursor: "pointer" }}
              />
            </div>
            <form onSubmit={handleAddEntry} className="add-customer-form">
              <div className="add-cutomer-input-container">
                <label className="cust-eng-create-title">First Name:</label>
                <input
                  type="text"
                  name="First Name"
                  className="add-cutomer-input"
                  value={newEntry["First Name"]}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="add-cutomer-input-container">
                <label className="cust-eng-create-title">Last Name:</label>
                <input
                  type="text"
                  name="Last Name"
                  value={newEntry["Last Name"]}
                  onChange={handleInputChange}
                  className="add-cutomer-input"
                  required
                />
              </div>

              <div className="add-cutomer-input-container">
                <label className="cust-eng-create-title">Email:</label>
                <input
                  type="email"
                  name="Email id"
                  value={newEntry["Email id"]}
                  onChange={handleInputChange}
                  className="add-cutomer-input"
                  required
                />
              </div>

              <div className="add-cutomer-input-container">
                <label className="cust-eng-create-title">Mobile No:</label>
                <input
                  type="tel"
                  name="Mobile No"
                  value={newEntry["Mobile No"]}
                  onChange={handleInputChange}
                  className="add-cutomer-input"
                />
              </div>

              <div className="add-cutomer-input-container ">
                <label className="cust-eng-create-title">Stage:</label>
                <select
                  name="Stage"
                  value={newEntry["Stage"]}
                  onChange={handleInputChange}
                  className="add-cutomer-input dropdown-input"
                >
                  <option value="Lead">Lead</option>
                  <option value="Customer">Customer</option>
                </select>
              </div>

              <div className="add-cutomer-input-container">
                <label className="cust-eng-create-title">Lead Date:</label>
                <input
                  type="date"
                  name="Lead Date"
                  value={newEntry["Lead Date"]}
                  onChange={handleInputChange}
                  className="add-cutomer-input"
                />
              </div>

              <div className="add-cutomer-input-container">
                <label className="cust-eng-create-title">Email Subscription:</label>
                <select
                  name="Email Subscription"
                  value={newEntry["Email Subscription"]}
                  onChange={handleInputChange}
                  className="add-cutomer-input dropdown-input"
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div className="add-cutomer-input-container">
                <label className="cust-eng-create-title">Other Details:</label>
                <textarea
                  name="Other Details"
                  value={newEntry["Other Details"]}
                  onChange={handleInputChange}
                  rows="4"
                  className="add-cutomer-input"
                />
              </div>

              <div className="add-cutomer-button-container">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  style={{
                    fontSize: "17px",
                    fontWeight: "500",
                    padding: "5px 10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#f8f9fa",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addLoading}
                  style={{
                    fontSize: "17px",
                    fontWeight: "450",
                    padding: "5px 10px",
                    border: "none",
                    borderRadius: "4px",
                    backgroundColor: "#28a745",
                    color: "white",
                    cursor: "pointer",
                    opacity: addLoading ? 0.6 : 1,
                  }}
                >
                  {addLoading ? "Adding..." : "Add Customer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!showAddForm && (
        <div className="engagement-content">
          {loading && (
            <div className="loading-container">
              <div className="loader"></div>
              <p>Loading customer data...</p>
            </div>
          )}

          {error && (
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Data</h3>
                <p>{error}</p>
                <button className="retry-btn" onClick={handleRefresh}>
                  Try Again
                </button>
              </div>
            </div>
          )}

          {!loading && !error && sheetData.length === 0 && (
            <div className="empty-state">
              <h3>No Customer Data Found</h3>
              <p>No contact submissions have been received yet.</p>
            </div>
          )}

          {!loading &&
            !error &&
            sheetData.length > 0 &&
            filteredData.length === 0 &&
            searchTerm && (
              <div className="empty-state">
                <h3>No Search Results</h3>
                <p>No customers found matching "{searchTerm}"</p>
                <button
                  className="clear-search-btn"
                  onClick={clearSearch}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                >
                  Clear Search
                </button>
              </div>
            )}

          {!loading &&
            !error &&
            filteredData.length > 0 &&
            !showCustomerDetails && (
              <div className="data-table-container">
                <div className="table-title">
                  Customers
                  {searchTerm && (
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "normal",
                        marginLeft: "10px",
                      }}
                    >
                      ({filteredData.length} of {sheetData.length} results for "
                      {searchTerm}")
                    </span>
                  )}
                </div>

                <div className="table-wrapper">
                  <table className="engagement-table">
                    <thead className="customer-table-header">
                      <tr>
                        <th className="header-name">Name</th>
                        <th className="header-email">Email</th>
                        <th className="header-stage">Stage</th>
                        <th className="header-action">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item, index) => (
                        <tr
                          key={index}
                          onClick={() => handleCustomerRowClick(item)}
                          style={{ cursor: "pointer" }}
                          className="customer-row"
                        >
                          <td className="name-cell">
                            <div className="name-container">
                              <span className="full-name">
                                {item["First Name"] || ""}{" "}
                                {item["Last Name"] || ""}
                              </span>
                              {!item["First Name"] && !item["Last Name"] && (
                                <span className="no-name">
                                  No name provided
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="email-cell">
                            {item["Email id"] ? (
                              <a
                                href={`mailto:${item["Email id"]}`}
                                className="email-link"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {item["Email id"]}
                              </a>
                            ) : (
                              <span className="no-data">No email</span>
                            )}
                          </td>
                          <td className="stage-cell">
                            <span
                              className={`stage-badge stage-${(
                                item["Stage"] || ""
                              ).toLowerCase()}`}
                            >
                              {item["Stage"] || "N/A"}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <button
                              className="update-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditCustomer(item);
                              }}
                              style={{
                                padding: "6px 12px",
                                backgroundColor: "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              Update
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {/* Customer Details View */}
          {showCustomerDetails && selectedCustomer && (
            <div className="customer-details-container">
              {!editingCustomer && (
                <div className="customer-details-header">
                  <div className="customer-detail-view-title">
                    <div className="empty-div"></div>
                    <div className="add-cutomer-title">Customer Details</div>
                    <img
                      width="48"
                      height="48"
                      src="https://img.icons8.com/fluency/48/delete-sign.png"
                      alt="delete-sign"
                      className="status-cross-symbol"
                      onClick={closeCustomerDetails}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                  <div className="details-actions">
                    <button
                      className="edit-customer-btn"
                      onClick={() => handleEditCustomer(selectedCustomer)}
                      style={{
                        padding: "8px 13px",
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        marginRight: "10px",
                      }}
                    >
                      Edit Customer
                    </button>
                    <button
                      className="close-details-btn"
                      onClick={closeCustomerDetails}
                      style={{
                        padding: "8px 13px",
                        backgroundColor: "#6c757d",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Back to List
                    </button>
                  </div>
                </div>
              )}

              {!editingCustomer ? (
                <div className="customer-details-view-container">
                  <div className="details-list-show">
                    <div className="detail-item">
                      <label className="cust-eng-create-title">Full Name:</label>
                      <span className="customer-data-show">
                        {selectedCustomer["First Name"] ||
                        selectedCustomer["Last Name"]
                          ? `${selectedCustomer["First Name"] || ""} ${
                              selectedCustomer["Last Name"] || ""
                            }`.trim()
                          : "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label className="cust-eng-create-title">Email:</label>
                      <span className="customer-data-show">
                        {selectedCustomer["Email id"] ? (
                          <a
                            href={`mailto:${selectedCustomer["Email id"]}`}
                            
                          >
                            {selectedCustomer["Email id"]}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label className="cust-eng-create-title">Mobile No:</label>
                      <span className="customer-data-show">
                        {selectedCustomer["Mobile No"] ? (
                          <a
                            href={`tel:${selectedCustomer["Mobile No"]}`}
                            
                          >
                            {selectedCustomer["Mobile No"]}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label className="cust-eng-create-title">Stage:</label>
                      <span
                        className={`customer-data-show stage-badge stage-${(
                          selectedCustomer["Stage"] || ""
                        ).toLowerCase()}`}
                      >
                        {selectedCustomer["Stage"] || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label className="cust-eng-create-title">Customer Date:</label>
                      <span className="customer-data-show">
                        {formatDate(selectedCustomer["Customer date"])}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label className="cust-eng-create-title">Email Subscription:</label>
                      <span
                        className={`customer-data-show subscription-badge ${
                          selectedCustomer["Email Subscription"] === "Yes"
                            ? "subscribed"
                            : "unsubscribed"
                        }`}
                      >
                        {selectedCustomer["Email Subscription"] || "N/A"}
                      </span>
                    </div>
                    {selectedCustomer["Other Details"] &&
                      selectedCustomer["Other Details"].trim() && (
                        <div className="detail-item full-width">
                          <label className="cust-eng-create-title">Other Details:</label>
                          <span className="customer-data-show">
                            {selectedCustomer["Other Details"]}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              ) : (
                <div className="edit-customer-form">
                  <div className="customer-close-btn">
                    <div className="empty-div"></div>
                    <div className="add-cutomer-title">Edit Customer</div>
                    <img
                      width="48"
                      height="48"
                      src="https://img.icons8.com/fluency/48/delete-sign.png"
                      alt="delete-sign"
                      className="status-cross-symbol"
                      onClick={() => setEditingCustomer(false)}
                      style={{ cursor: "pointer" }}
                    />
                  </div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSaveEdit();
                    }}
                    className="add-customer-form"
                  >
                    <div className="add-cutomer-input-container">
                      <label className="cust-eng-create-title">First Name:</label>
                      <input
                        type="text"
                        name="First Name"
                        value={editCustomerData["First Name"] || ""}
                        onChange={handleEditInputChange}
                        className="add-cutomer-input"
                      />
                    </div>
                    <div className="add-cutomer-input-container">
                      <label className="cust-eng-create-title">Last Name:</label>
                      <input
                        type="text"
                        name="Last Name"
                        value={editCustomerData["Last Name"] || ""}
                        onChange={handleEditInputChange}
                        className="add-cutomer-input"
                      />
                    </div>
                    <div className="add-cutomer-input-container">
                      <label className="cust-eng-create-title">Email:</label>
                      <input
                        type="email"
                        name="Email id"
                        value={editCustomerData["Email id"] || ""}
                        onChange={handleEditInputChange}
                        className="add-cutomer-input"
                      />
                    </div>
                    <div className="add-cutomer-input-container">
                      <label className="cust-eng-create-title">Mobile No:</label>
                      <input
                        type="tel"
                        name="Mobile No"
                        value={editCustomerData["Mobile No"] || ""}
                        onChange={handleEditInputChange}
                        className="add-cutomer-input"
                      />
                    </div>
                    <div className="add-cutomer-input-container">
                      <label className="cust-eng-create-title">Stage:</label>
                      <select
                        name="Stage"
                        value={editCustomerData["Stage"] || ""}
                        onChange={handleEditInputChange}
                        className="add-cutomer-input dropdown-input"
                      >
                        <option value="Lead">Lead</option>
                        <option value="Customer">Customer</option>
                        <option value="Lost">Lost</option>
                      </select>
                    </div>
                    <div className="add-cutomer-input-container">
                      <label className="cust-eng-create-title">Lead Date:</label>
                      <input
                        type="date"
                        name="Lead Date"
                        value={editCustomerData["Lead Date"] || ""}
                        onChange={handleEditInputChange}
                        className="add-cutomer-input"
                      />
                    </div>
                    <div className="add-cutomer-input-container">
                      <label className="cust-eng-create-title">Email Subscription:</label>
                      <select
                        name="Email Subscription"
                        value={editCustomerData["Email Subscription"] || ""}
                        onChange={handleEditInputChange}
                        className="add-cutomer-input dropdown-input"
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                    <div className="add-cutomer-input-container">
                      <label className="cust-eng-create-title">Other Details:</label>
                      <textarea
                        name="Other Details"
                        value={editCustomerData["Other Details"] || ""}
                        onChange={handleEditInputChange}
                        rows="4"
                        className="add-cutomer-input"
                      />
                    </div>
                    <div className="add-cutomer-button-container">
                      <button
                        type="button"
                        onClick={() => setEditingCustomer(false)}
                        style={{
                          fontSize: "17px",
                          fontWeight: "500",
                          padding: "5px 10px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#f8f9fa",
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={updateLoading}
                        style={{
                          fontSize: "17px",
                          fontWeight: "450",
                          padding: "5px 10px",
                          border: "none",
                          borderRadius: "4px",
                          backgroundColor: "#28a745",
                          color: "white",
                          cursor: "pointer",
                          opacity: updateLoading ? 0.6 : 1,
                        }}
                      >
                        {updateLoading ? "Updating..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
